import { useState } from 'react'
import { CreditCard, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Section } from '@/components/manager/overview/Section'
import { usePaymentMethod } from '@/api/stripe'
import { CardBrandIcon } from './CardBrandIcon'
import { UpdateCardDialog } from './UpdateCardDialog'

/**
 * Carte de paiement actuelle + bouton d'édition (Stripe Elements inline).
 *
 * Flow update :
 *   1. Click "Modifier" → ouvre dialog
 *   2. Dialog: crée SetupIntent backend → reçoit client_secret
 *   3. Elements monte <PaymentElement> avec ce secret
 *   4. Submit: confirmSetup → si OK, set-default-payment-method backend
 *   5. Invalide la query payment-method → refresh affichage
 */
interface Props {
  darkMode?: boolean
}

export function PaymentMethodCard({ darkMode = false }: Props) {
  const { t } = useTranslation()
  const { data, isPending, isError } = usePaymentMethod()
  const [editing, setEditing] = useState(false)

  return (
    <>
      <Section
        title={t('profile.billing.paymentMethodTitle', 'Méthode de paiement')}
        icon={<CreditCard className="h-5 w-5" />}
      >
        {isPending ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        ) : isError ? (
          <p className="text-sm text-red-500 py-4">
            {t(
              'profile.billing.paymentMethodError',
              'Impossible de charger la méthode de paiement.',
            )}
          </p>
        ) : (
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <CardBrandIcon
                brand={data?.brand}
                className="h-7 w-11 flex-shrink-0"
              />
              <div className="min-w-0">
                {data?.has_payment_method && data.brand === 'link' ? (
                  <>
                    <p className="text-sm font-medium text-slate-900 dark:text-white">Link</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{data.label}</p>
                  </>
                ) : data?.has_payment_method ? (
                  <>
                    <p className="text-sm font-medium text-slate-900 dark:text-white tabular-nums">
                      •••• •••• •••• {data.last4}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">
                      {t('profile.billing.expires', 'Expire')}{' '}
                      {pad(data.exp_month ?? undefined)}/{data.exp_year}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {t(
                      'profile.billing.noPaymentMethod',
                      'Aucune méthode de paiement enregistrée',
                    )}
                  </p>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing(true)}
              className={darkMode ? 'border-slate-700' : ''}
            >
              {data?.has_payment_method
                ? t('profile.billing.edit', 'Modifier')
                : t('profile.billing.add', 'Ajouter')}
            </Button>
          </div>
        )}
      </Section>

      <UpdateCardDialog
        open={editing}
        onClose={() => setEditing(false)}
        darkMode={darkMode}
      />
    </>
  )
}

function pad(n: number | undefined): string {
  if (!n) return '--'
  return n < 10 ? `0${n}` : `${n}`
}
